import api from '../config/api';

class LeadAssignmentNotificationService {
  /**
   * Get unread assignment notifications for current user
   * @returns {Promise<Object>} { success, data, count }
   */
  static async getUnreadNotifications() {
    try {
      const response = await api.get('/lead-assignment-notifications/unread');
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error fetching unread notifications:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Failed to fetch notifications'
      };
    }
  }

  /**
   * Get all assignment notifications for current user
   * @param {number} limit - Number of notifications to fetch
   * @returns {Promise<Object>} { success, data, count }
   */
  static async getAllNotifications(limit = 50) {
    try {
      const response = await api.get(`/lead-assignment-notifications/all?limit=${limit}`);
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error fetching all notifications:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Failed to fetch notifications'
      };
    }
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} { success, message }
   */
  static async markAsRead(notificationId) {
    try {
      const response = await api.put(`/lead-assignment-notifications/${notificationId}/read`);
      
      // Dispatch event to update UI
      window.dispatchEvent(new CustomEvent('notification-read', { 
        detail: { notificationId } 
      }));
      
      return {
        success: true,
        message: response.data.message || 'Notification marked as read'
      };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to mark notification as read'
      };
    }
  }

  /**
   * Mark all notifications as read for current user
   * @returns {Promise<Object>} { success, message, count }
   */
  static async markAllAsRead() {
    try {
      const response = await api.put('/lead-assignment-notifications/mark-all-read');
      
      // Dispatch event to update UI
      window.dispatchEvent(new Event('all-notifications-read'));
      
      return {
        success: true,
        message: response.data.message || 'All notifications marked as read',
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to mark all notifications as read',
        count: 0
      };
    }
  }

  /**
   * Delete notification
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} { success, message }
   */
  static async deleteNotification(notificationId) {
    try {
      const response = await api.delete(`/lead-assignment-notifications/${notificationId}`);
      
      // Dispatch event to update UI
      window.dispatchEvent(new CustomEvent('notification-deleted', { 
        detail: { notificationId } 
      }));
      
      return {
        success: true,
        message: response.data.message || 'Notification deleted'
      };
    } catch (error) {
      console.error('Error deleting notification:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete notification'
      };
    }
  }

  /**
   * Get notification count for current user
   * @returns {Promise<Object>} { success, unreadCount, totalCount }
   */
  static async getNotificationCount() {
    try {
      const response = await api.get('/lead-assignment-notifications/count');
      return {
        success: true,
        unreadCount: response.data.unreadCount || 0,
        totalCount: response.data.totalCount || 0
      };
    } catch (error) {
      console.error('Error fetching notification count:', error);
      return {
        success: false,
        unreadCount: 0,
        totalCount: 0,
        error: error.response?.data?.message || 'Failed to fetch notification count'
      };
    }
  }

  /**
   * Create notification when lead is assigned (called after assignment)
   * This is typically called from the assignment service
   * @param {Object} assignmentData - Assignment details
   * @returns {Promise<Object>} { success, message }
   */
  static async createAssignmentNotification(assignmentData) {
    try {
      const response = await api.post('/lead-assignment-notifications/create', assignmentData);
      
      // Dispatch event to notify UI
      window.dispatchEvent(new Event('lead-assigned'));
      
      return {
        success: true,
        message: response.data.message || 'Notification created',
        data: response.data.data
      };
    } catch (error) {
      console.error('Error creating assignment notification:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create notification'
      };
    }
  }

  /**
   * Format notification for display
   * @param {Object} notification - Notification object from database
   * @returns {Object} Formatted notification
   */
  static formatNotification(notification) {
    return {
      ...notification,
      formattedTime: new Date(notification.createdAt).toLocaleString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short'
      }),
      priorityColor: this.getPriorityColor(notification.priority),
      priorityIcon: this.getPriorityIcon(notification.priority)
    };
  }

  /**
   * Get priority color class
   * @param {string} priority - Priority level
   * @returns {string} CSS class
   */
  static getPriorityColor(priority) {
    const colors = {
      low: 'text-green-600 bg-green-100',
      medium: 'text-yellow-600 bg-yellow-100',
      high: 'text-orange-600 bg-orange-100',
      urgent: 'text-red-600 bg-red-100'
    };
    return colors[priority] || colors.medium;
  }

  /**
   * Get priority icon
   * @param {string} priority - Priority level
   * @returns {string} Icon emoji
   */
  static getPriorityIcon(priority) {
    const icons = {
      low: '✅',
      medium: '📌',
      high: '⚡',
      urgent: '🔥'
    };
    return icons[priority] || '📌';
  }
}

export default LeadAssignmentNotificationService;
